<template>
  <div class="footer-en" data-buried="Footer" :class="{ 'footer-en--retractable': retractable }">
    <div class="footer-en__content">
      <div class="footer-en__left">
        <div class="footer-en__logo pc-only">
          <Resource
            class="footer-en__logo-image"
            :src="LogoImg"
            alt="Rendora Logo"
            fetchpriority="low"
            @click="gotoHome"
          />
        </div>
        <div class="footer-en__left-content">
          <div class="footer-en__social-medias m-only">
            <div class="footer-en__social-title">Follow us</div>
            <div class="footer-en__social-links">
              <div
                v-for="account in mediaAccounts"
                :key="account.link"
                class="footer-en__social-item"
              >
                <component
                  :is="account.icon"
                  :key="account.link"
                  :font-controlled="false"
                  @click="goto(account.link)"
                ></component>
              </div>
            </div>
            <div class="footer-en__social-medias-divider"></div>
          </div>
          <div class="footer-en__logo m-only">
            <Resource
              class="footer-en__logo-image"
              :src="LogoImg"
              alt="Rendora Logo"
              fetchpriority="low"
              @click="gotoHome"
            />
          </div>
          <div class="footer-en__description">
            Rendora is the world #1 3D AI Avatar Video Platform. <br />
            Powering with AI technology, Rendora enables <br />
            everyone to create studio-quality videos in seconds <br />
            without cameras, mics, actors, and studios.
          </div>
          <div class="footer-en__social-medias pc-only">
            <div class="footer-en__social-title">Follow us</div>
            <div class="footer-en__social-links">
              <div
                v-for="account in mediaAccounts"
                :key="account.link"
                class="footer-en__social-item"
              >
                <component
                  :is="account.icon"
                  :key="account.link"
                  :font-controlled="false"
                  @click="goto(account.link)"
                ></component>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="footer-en__links">
        <div class="footer-en__column">
          <div class="footer-en__column-title">Products & Resources</div>
          <ul class="footer-en__list">
            <li v-for="item in productLinks" :key="item.name" class="footer-en__list-item">
              <a class="footer-en__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-en__column">
          <div class="footer-en__column-title">Solutions</div>
          <ul class="footer-en__list">
            <li v-for="item in useCaseLinks" :key="item.name" class="footer-en__list-item">
              <a class="footer-en__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-en__column">
          <div class="footer-en__column-title">Academy</div>
          <ul class="footer-en__list">
            <li v-for="item in academyLinks" :key="item.name" class="footer-en__list-item">
              <a class="footer-en__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-en__column">
          <div class="footer-en__column-title">Support</div>
          <ul class="footer-en__list">
            <li v-for="item in supportLinks" :key="item.name" class="footer-en__list-item">
              <a class="footer-en__link" :href="item.url" :target="item.target">{{ item.name }}</a>
            </li>
          </ul>
        </div>

        <div class="footer-en__column footer-en__column--company">
          <div class="footer-en__column-title">Company</div>
          <ul class="footer-en__list">
            <li class="footer-en__list-item footer-en__contact-item">
              <iconpark-icon name="mail" size="16"></iconpark-icon>
              Support: contact@rendora.ai
            </li>
            <li class="footer-en__list-item footer-en__contact-item">
              <iconpark-icon name="building" size="16"></iconpark-icon>
              Company: Rendora LLC
            </li>
            <li class="footer-en__list-item footer-en__contact-item">
              <iconpark-icon name="map-pin" size="16" class="footer-en__map-icon"></iconpark-icon>
              Address: 1209 Orange Street, Wilmington, New Castle County, Delaware
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="footer-en__agreements">
      <div v-for="item in linkList" :key="item.name" class="footer-en__agreement-item">
        <a class="footer-en__agreement-link" :href="item.url" :target="item.target">
          {{ item.name }}
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Resource from '~/components/resource/index';
import { getRedirectLink } from '~/utils/landing';
import { getFeUrl } from '~/utils/url';
import LinkedinSvg from './linkedin.svg';
import TwitterSvg from './twitter.svg';
import YoutubeSvg from './youtube.svg';

withDefaults(
  defineProps<{
    retractable?: boolean;
  }>(),
  {
    retractable: true,
  },
);

const LogoImg = getFeUrl('/rendora/main/footer/logo-black.png');

const productLinks = [
  {
    name: 'Home',
    url: '/',
    target: '_self',
  },
  {
    name: '3D Avatars',
    url: '/3d-ai-avatar',
    target: '_self',
  },
  {
    name: '3D Avatar Hub',
    url: '/characters',
    target: '_self',
  },
  {
    name: 'AI Video Generation',
    url: '/ai-avatar-video-generation',
    target: '_self',
  },
  {
    name: 'Gallery',
    url: '/template',
    target: '_self',
  },
  {
    name: 'AI Voice',
    url: '/ai-voice',
    target: '_self',
  },
  {
    name: 'Studio',
    url: '/scene',
    target: '_self',
  },
];

const mediaAccounts: { icon: string; link: string }[] = [
  {
    icon: TwitterSvg,
    link: 'https://x.com/RendoraAI',
  },
  {
    icon: YoutubeSvg,
    link: 'https://www.youtube.com/channel/UCiVeyPccijStmOlCOD_IRnw',
  },
  {
    icon: LinkedinSvg,
    link: 'https://www.linkedin.com/company/rendora-ai/',
  },
];

const scenariosTags = useScenariosTags();

const useCaseLinks = computed(() => {
  return scenariosTags.value.map(tag => ({
    name: tag.tag_name,
    url: `/scenarios/${tag.tag_outer_id}`,
    target: '_self',
  }));
});

const linkList = [
  {
    name: 'Privacy Policy',
    url: 'https://protocol.rendora.ai/legal/privacy-policy',
    target: '_blank',
  },
  {
    name: 'User Agreement',
    url: 'https://protocol.rendora.ai/legal/user',
    target: '_blank',
  },
  {
    name: 'Software Purchase Agreement',
    url: 'https://protocol.rendora.ai/legal/software',
    target: '_blank',
  },
];
const academyLinks = [
  {
    name: 'About Rendora AI',
    url: '/academy/1-1.html',
    target: '_self',
  },
  {
    name: 'Tutorials',
    url: '/tutorials',
    target: '_self',
  },
];

const supportLinks = [
  {
    name: 'Pricing',
    url: '/subscribe',
    target: '_self',
  },
  {
    name: 'Help Center',
    url: '/help-center',
    target: '_self',
  },
  {
    name: 'Quick Start',
    url: '/help-center/9-25.html',
    target: '_self',
  },
  {
    name: 'User Guide',
    url: '/help-center/10-31.html',
    target: '_self',
  },
  {
    name: 'FAQs',
    url: '/help-center/5-5.html',
    target: '_self',
  },
  {
    name: 'Latest Updates',
    url: '/help-center/8-13.html',
    target: '_self',
  },
  {
    name: 'Enterprise Kit',
    url: '/help-center/11-24.html',
    target: '_self',
  },
  {
    name: 'SOC 2',
    url: '/help-center/8-36.html',
    target: '_self',
  },
];

function goto(link: string) {
  window.open(link);
}

function gotoHome() {
  window.location.href = getRedirectLink(window.location.origin, true);
}
</script>

<style lang="scss" scoped src="./en-styles/mobile.scss" />
<style lang="scss" scoped src="./en-styles/pc.scss" />
